import type { NotificationTypeKey } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiClient } from '@/lib/api-client';
import { useIsSaasAdmin } from '@/modules/auth/hooks';

export const NOTIFICATIONS_ROUTES = { LIST: '/notificacoes' } as const;

const ENDPOINT = '/api/v1/notifications';

export interface Notification {
  id: string;
  type: NotificationTypeKey;
  title: string;
  body: string;
  link: string | null;
  entityId: string | null;
  read: boolean;
  createdAt: string;
}

export function useNotifications(params: { unread?: boolean; limit?: number } = {}) {
  const busca = new URLSearchParams();
  if (params.unread) busca.set('unread', 'true');
  if (params.limit) busca.set('limit', String(params.limit));

  // Notificacao e da empresa: quem administra o SaaS nao tem uma, e sem isto
  // o sino insistiria em 403 a cada trinta segundos.
  const semEmpresa = useIsSaasAdmin();

  return useQuery({
    queryKey: ['notifications', params],
    enabled: !semEmpresa,
    queryFn: () => apiClient.get<{ data: Notification[] }>(`${ENDPOINT}?${busca}`),
    // O SSE avisa na hora; o polling e a rede de seguranca se a conexao cair.
    refetchInterval: 30_000,
  });
}

export function useUnreadCount() {
  const semEmpresa = useIsSaasAdmin();

  return useQuery({
    queryKey: ['notifications-count'],
    enabled: !semEmpresa,
    queryFn: () => apiClient.get<{ total: number }>(`${ENDPOINT}/unread-count`),
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.post<Notification>(`${ENDPOINT}/${id}/read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.post<{ updated: number }>(`${ENDPOINT}/read-all`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });
}

/**
 * Escuta o aviso do servidor. Sem isso a pendencia so apareceria no proximo
 * polling - trinta segundos depois de ja estar atrasada.
 */
export function useNotificationsStream(): void {
  const queryClient = useQueryClient();
  const semEmpresa = useIsSaasAdmin();

  useEffect(() => {
    if (semEmpresa) return;

    const fonte = new EventSource(`${ENDPOINT}/stream`, { withCredentials: true });

    fonte.onmessage = () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    };

    // O navegador ja reconecta sozinho no `retry` que o servidor manda; o
    // handler existe para nao vazar erro no console.
    fonte.onerror = () => {};

    return () => fonte.close();
  }, [queryClient, semEmpresa]);
}
